"use client";

import * as React from "react";
import { Music2 } from "lucide-react";
import { cn } from "@gerama/utils";
import { MediaImage } from "@/components/media-image";

/** A stable hue from any string — the fallback tint when the cover can't be sampled. */
function hueOf(seed: string): number {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) hash = (hash * 31 + seed.charCodeAt(i)) | 0;
  return Math.abs(hash) % 360;
}

/**
 * The cover's average colour (Spotify tints every header with it). Sampled
 * from a tiny canvas; a cover served without CORS headers taints the canvas,
 * so that — or no cover at all — falls back to a hue derived from [seed].
 */
export function useCoverColor(src: string | null | undefined, seed: string): string {
  const [color, setColor] = React.useState(() => `hsl(${hueOf(seed)} 45% 35%)`);

  React.useEffect(() => {
    setColor(`hsl(${hueOf(seed)} 45% 35%)`);
    if (!src || !/^https?:\/\//.test(src)) return;
    let cancelled = false;
    const img = new window.Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = canvas.height = 8;
        const ctx = canvas.getContext("2d");
        if (!ctx) return;
        ctx.drawImage(img, 0, 0, 8, 8);
        const data = ctx.getImageData(0, 0, 8, 8).data;
        let r = 0;
        let g = 0;
        let b = 0;
        for (let i = 0; i < data.length; i += 4) {
          r += data[i]!;
          g += data[i + 1]!;
          b += data[i + 2]!;
        }
        const n = data.length / 4;
        // Darken a little so white text stays readable on light covers.
        const k = 0.75;
        if (!cancelled)
          setColor(
            `rgb(${Math.round((r / n) * k)} ${Math.round((g / n) * k)} ${Math.round((b / n) * k)})`,
          );
      } catch {
        // Tainted canvas (no CORS on the image host) — keep the fallback.
      }
    };
    img.src = src;
    return () => {
      cancelled = true;
    };
  }, [src, seed]);

  return color;
}

interface DetailHeroProps {
  /** "آلبوم", "پلی‌لیست", "هنرمند"… */
  kind: string;
  title: string;
  cover: string | null | undefined;
  /** Colour seed when the cover can't be sampled — usually `${type}-${id}`. */
  seed: string;
  round?: boolean;
  /** Line under the title (artists · year · n songs). */
  meta?: React.ReactNode;
  description?: React.ReactNode;
  /** Play/follow buttons, drawn on the action row under the header. */
  actions?: React.ReactNode;
  fallbackIcon?: React.ReactNode;
}

/** Spotify's detail header: big cover, huge title, tinted gradient fading into the page. */
export function DetailHero({
  kind,
  title,
  cover,
  seed,
  round,
  meta,
  description,
  actions,
  fallbackIcon,
}: DetailHeroProps) {
  const color = useCoverColor(cover, seed);
  return (
    <header className="relative -mx-4 -mt-6 sm:mx-0 sm:mt-0">
      <div
        className="rounded-none px-4 pt-8 pb-6 transition-colors duration-700 sm:rounded-xl sm:px-6"
        style={{
          background: `linear-gradient(180deg, ${color} 0%, color-mix(in oklab, ${color} 35%, transparent) 70%, transparent 100%)`,
        }}
      >
        <div className="flex flex-col items-center gap-5 text-center sm:flex-row sm:items-end sm:text-start">
          <div
            className={cn(
              "bg-muted relative size-44 shrink-0 overflow-hidden shadow-[0_8px_40px_rgba(0,0,0,0.5)] sm:size-52 lg:size-56",
              round ? "rounded-full" : "rounded-md",
            )}
          >
            {cover ? (
              <MediaImage
                src={cover}
                alt={title}
                fill
                sizes="224px"
                className="object-cover"
                priority
              />
            ) : (
              <span className="text-muted-foreground absolute inset-0 flex items-center justify-center">
                {fallbackIcon ?? <Music2 className="size-14" />}
              </span>
            )}
          </div>
          <div className="min-w-0 space-y-2 text-white">
            <p className="text-sm font-semibold drop-shadow">{kind}</p>
            <h1
              className={cn(
                "font-black tracking-tight break-words drop-shadow-md",
                title.length > 28 ? "text-3xl sm:text-4xl" : "text-4xl sm:text-6xl lg:text-7xl",
              )}
            >
              {title}
            </h1>
            {description && <div className="text-sm text-white/80">{description}</div>}
            {meta && <div className="text-sm font-medium text-white/90">{meta}</div>}
          </div>
        </div>
      </div>
      {actions && <div className="flex items-center gap-4 px-4 py-4 sm:px-6">{actions}</div>}
    </header>
  );
}
